import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import Breadcrumbs from '@pelckmans/elementis/components/breadcrumbs';
import Page from '@pelckmans/elementis/components/page';

import { getMedialinks, Medialink } from '../../api/medialink';
import { getModuleById, Module } from '../../api/module';
import { fetchTocNodes, NodeWithoutDescription } from '../../api/table-of-content';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useThrowAsyncError from '../../hooks/useThrowAsyncError';
import ContentBody from '../../layout/content/ContentBody';
import ContentHeader from '../../layout/content/ContentHeader';
import Footer from '../../layout/footer/Footer';
import ModuleAlerts from '../../shared-components/alerts/ModuleAlerts';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../shared-components/elementis/hoc/withScrollThreshold';
import { groupMedialinksByNode } from '../../utils/medialink';
import { flattenNodes } from '../../utils/tocNode';
import MedialinkSection from './components/MedialinkSection';
import ContentFooter from '../../layout/content/ContentFooter';

function LinkedMaterialList({ pastThreshold }: WithScrollThresholdProps) {
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();
  const location = useLocation();

  const [searchParams] = useSearchParams();

  useDocumentTitle(t('PAGES.LINKED_MATERIAL_LIST.DOCUMENT_TITLE'));

  const moduleId = searchParams.get('moduleId') || '';
  const medialinkIds = searchParams.get('medialinkIds') || '';

  const [medialinksByNodeId, setMedialinksByNodeId] = useState<Record<string, Medialink[]>>();
  const [module, setModule] = useState<Module>();
  const [superModule, setSuperModule] = useState<Module>();
  const [loading, setLoading] = useState<boolean>(false);
  const [flattenedTocNodes, setFlattenedTocNodes] = useState<NodeWithoutDescription[]>();

  const stableRefetch = useCallback(async () => {
    const { data } = await getMedialinks(moduleId, { ids: medialinkIds });
    setMedialinksByNodeId(groupMedialinksByNode(data));
  }, [medialinkIds, moduleId]);

  useEffect(() => {
    if (moduleId) return;
    handleError(new Error('Module not found'));
  }, [handleError, moduleId]);

  useEffect(() => {
    if (!medialinkIds || !moduleId) return;
    setLoading(true);

    async function fetchMedialinks() {
      try {
        const [{ data: _medialinks }, _module, tocNodes] = await Promise.all([
          getMedialinks(moduleId, { ids: medialinkIds }),
          getModuleById(moduleId),
          fetchTocNodes(moduleId),
        ]);

        if (_module.superModuleId) {
          const _superModule = await getModuleById(_module.superModuleId);
          setSuperModule(_superModule);
        } else {
          setSuperModule(_module);
        }

        setMedialinksByNodeId(groupMedialinksByNode(_medialinks));
        setModule(_module);
        setFlattenedTocNodes(flattenNodes(tocNodes.data));
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    }

    fetchMedialinks();
  }, [handleError, medialinkIds, moduleId]);

  if (!moduleId) {
    return null;
  }

  return (
    <div className={classNames('page', 'linked-material-list-page')}>
      <div className="page-content">
        <div className="main-layout">
          <div className="main-layout-container">
            <div className="content-layout">
              <main>
                <div className="margin-wrapper">
                  <ContentHeader
                    cover={
                      <Page.Header
                        density={pastThreshold ? 'compact' : 'normal'}
                        headerText={t('PAGES.LINKED_MATERIAL_LIST.PAGE_TITLE')}
                        breadcrumbs={
                          superModule ? (
                            <Breadcrumbs aria-label={t('MODULE_BREADCRUMB.A11Y_LABEL')}>
                              <Breadcrumbs.Item
                                avatarImage={
                                  superModule.styling?.logo ? (
                                    <AvatarImage name={superModule.name} src={superModule.styling?.logo} />
                                  ) : undefined
                                }
                                to={`${location.pathname}${location.search}${location.hash}`}
                              >
                                {superModule.name}
                              </Breadcrumbs.Item>
                            </Breadcrumbs>
                          ) : undefined
                        }
                      />
                    }
                  />
                  <ContentBody alerts={module ? <ModuleAlerts module={module} /> : null}>
                    <div className="max-container overview-mode">
                      <div className="list-sections">
                        <MedialinkSection
                          medialinksByNodeId={medialinksByNodeId}
                          module={module}
                          superModule={superModule}
                          flattenedTocNodes={flattenedTocNodes}
                          loading={loading}
                          refetch={stableRefetch}
                        />
                      </div>
                    </div>
                  </ContentBody>
                  <ContentFooter>
                    <Footer />
                  </ContentFooter>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withScrollThreshold(LinkedMaterialList);
